'use client';

import { useCallback, useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useSession } from 'next-auth/react';
import { useTheme } from 'next-themes';
import { ProfileControl } from '@owlcat/identity-react';
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@owlcat/ui';
import { locales, localeNames, type Locale } from '@/shared/i18n';
import { updatePreferredLocale } from '@/contexts/family';
import { openSubscription } from '@/contexts/subscriptions/lib/accountsTopUp';
import { useAllowanceMeter } from '@/contexts/subscriptions/hooks/useAllowanceMeter';
import AllowanceMeter from '@/contexts/subscriptions/components/AllowanceMeter';
import { useEventSourceEvent } from '@/shared/hooks/useEventSourceEvent';
import { accountsProfileUrl, publicOrigin } from '@/shared/utils/publicOrigin';

/**
 * PortalHeaderControls — portal's fleet-shared identity/lang/theme/balance
 * cluster. Renders the @owlcat/identity-react <ProfileControl> DIRECTLY (like
 * shop's HeaderControls) so the portal header matches the rest of the fleet:
 * language + theme live INSIDE the profile dropdown, and a token-balance chip
 * sits beside the avatar (stories is token-relevant).
 *
 *   • AUTHED → [balance chip] ( avatar ▾ )
 *              dropdown: Profile · Balance+Buy · Language ▸ · Theme · Sign out
 *   • ANON   → [language][theme] [Sign in]
 *
 * We do NOT use @owlcat/app-shell <AppHeaderControls> here on purpose:
 *   1. Its theme controls call @owlcat/theme's useTheme, which resolves to a
 *      SEPARATE next-themes copy (packages/theme/node_modules) from portal's own
 *      next-themes ThemeProvider → a split React context that would leave the
 *      toggle inert. We use portal's own next-themes useTheme instead, so it
 *      shares portal's ThemeProvider context (theme system left intact).
 *   2. Its balance auto-fetch hits accounts `/me/balance`, but portal is
 *      subscription-first with HIDDEN tokens — so we show the <AllowanceMeter>
 *      (% + content estimates) and a subscribe/manage link, never a raw balance.
 *
 * Identity resolution (credentialed cross-subdomain GET /me, sign-out POST
 * /logout) is unchanged — portal's host-only NextAuth session + epoch
 * kill-switch stay intact.
 */

const localeFlags: Record<Locale, string> = {
  ru: '🇷🇺',
  en: '🇺🇸',
  cs: '🇨🇿',
  uk: '🇺🇦',
  de: '🇩🇪',
  fr: '🇫🇷',
};

const stripSlash = (u: string) => u.replace(/\/+$/, '');

/** Browser-reachable accounts origin. Prefer NEXT_PUBLIC_ACCOUNTS_URL; dev loopback fallback. */
function publicAccountsUrl(): string {
  return stripSlash(process.env.NEXT_PUBLIC_ACCOUNTS_URL || 'http://127.0.0.1:3200');
}

/** `compact` = the avatar alone, for the folded sidebar rail. */
export function PortalHeaderControls({ compact = false }: { compact?: boolean } = {}) {
  const locale = useLocale();
  const t = useTranslations('common');
  const tAllow = useTranslations('subscription.allowance');
  const pathname = usePathname();
  const { data: session, update } = useSession();
  const su = session?.user as { identityId?: string; name?: string | null; email?: string | null; image?: string | null } | undefined;
  const { resolvedTheme, setTheme } = useTheme();
  // Hidden-tokens model: the dropdown shows the allowance % (never a raw count).
  // One shared read (`useAllowanceMeter`) with the header chip and the notes —
  // three of them render on the same page and used to ask the server three times.
  const { data: allowance } = useAllowanceMeter();
  const percent = allowance ? Math.round(Math.max(0, Math.min(1, allowance.percent)) * 100) : null;
  const hasAllowance = !!allowance?.hasAllowance;
  const hasSubscription = !!allowance?.hasSubscription;
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const isDark = resolvedTheme === 'dark';

  const accounts = publicAccountsUrl();
  /**
   * The origin comes from the build, not from `window`.
   *
   * Reading `window.location.origin` during render is a server/client branch:
   * the HTML went out with `return=` empty and the browser rendered the real
   * URL, which React reports as a hydration mismatch and does NOT patch up —
   * so the link stayed broken. See `publicOrigin`.
   */
  const origin = publicOrigin();
  // Where accounts sends the user back to after login/logout — current portal URL.
  const returnTo = origin ? `${origin}${pathname}` : undefined;
  // accounts /profile deep-link: locale-matched + validated back-to-portal button.
  const profileUrl = accountsProfileUrl({ locale });
  // Subscription management lives ONLY in accounts — deep-link to accounts
  // /billing with app=portal + the current portal url as return.

  // Mirror portal's LanguageSelector navigation so switching still persists the
  // account preference (refreshing the JWT claim) before the full-page hop.
  const switchLocale = async (next: string) => {
    if (next === locale) return;
    if (session?.user) {
      try {
        await updatePreferredLocale(next);
        await update();
      } catch {
        // Continue with redirect even if the session update fails.
      }
    }
    const match = pathname.match(/^\/([a-z]{2})(\/.*)?$/);
    const newPathname = match
      ? `/${next}${match[2] || ''}`
      : `/${next}${pathname === '/' ? '' : pathname}`;
    window.location.href = newPathname;
  };

  // AUTHED dropdown extras: allowance summary + manage-subscription link,
  // language submenu, theme toggle. NO raw token count (hidden-tokens model).
  const menuExtras = (
    <>
      <DropdownMenuItem onSelect={() => openSubscription(locale)} data-testid="profile-subscribe" className="flex items-center gap-2">
          <span className="font-medium text-primary">
            {hasSubscription ? tAllow('manageSubscription') : tAllow('subscribe')}
          </span>
          {hasAllowance && percent != null ? (
            <span className="text-xs text-muted-foreground">· {percent}%</span>
          ) : null}
      </DropdownMenuItem>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger data-testid="profile-language-menu">
          {t('language')}: {localeNames[locale as Locale] ?? locale}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {locales.map((l) => (
            <DropdownMenuItem key={l} onSelect={() => void switchLocale(l)} data-testid={`profile-language-${l}`}>
              {localeFlags[l]} {localeNames[l]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuItem
        onSelect={(e) => {
          e.preventDefault();
          setTheme(isDark ? 'light' : 'dark');
        }}
        data-testid="profile-theme-toggle"
      >
        {t('toggleTheme')}
        {mounted ? (
          <span className="ml-auto text-xs text-muted-foreground">{isDark ? '◐' : '☀'}</span>
        ) : null}
      </DropdownMenuItem>
    </>
  );

  // ANON has no dropdown → compact language + theme beside the Sign-in button.
  const anonExtras = (
    <div className="flex items-center gap-2">
      <select
        aria-label={t('selectLanguage')}
        data-testid="header-language-select"
        value={locale}
        onChange={(e) => void switchLocale(e.target.value)}
        className="h-9 rounded-lg border border-border bg-background px-2 text-sm hover:bg-muted transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
      >
        {locales.map((l) => (
          <option key={l} value={l}>
            {localeFlags[l]}
          </option>
        ))}
      </select>
      <button
        type="button"
        data-testid="header-theme-toggle"
        aria-label={t('toggleTheme')}
        title={t('toggleTheme')}
        onClick={() => setTheme(isDark ? 'light' : 'dark')}
        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors text-sm"
      >
        {mounted ? (isDark ? '☀' : '◐') : ''}
      </button>
    </div>
  );

  return (
    <ProfileControl
      compact={compact}
      locale={locale}
      accountsUrl={accounts}
      appId="portal"
      selfPath="/me"
      loginPath="/login"
      logoutPath="/logout"
      profileUrl={profileUrl}
      returnTo={returnTo}
      // the app's own (accounts-resolved) session seeds the header: no flicker, no "sign in" on a
      // network hiccup; an explicit 401 from accounts /me still signs the header out
      initialProfile={su?.identityId ? { identityId: su.identityId, name: su.name ?? null, email: su.email ?? null, avatar: su.image ?? null } : null}
      labels={{
        signIn: t('signIn'),
        signOut: t('signOut'),
        profile: t('profile'),
        yourApps: t('yourApps'),
        account: t('account'),
      }}
      /**
       * The folded rail is one avatar wide.
       *
       * The meter chip beside the avatar left room for neither, so folded there
       * is only the profile — and the allowance is one tap away inside its
       * dropdown, where the percentage and the "≈ N left" estimates already
       * live.
       */
      headerExtras={compact ? undefined : <AllowanceMeter />}
      menuExtras={menuExtras}
      anonExtras={anonExtras}
    />
  );
}
